import { useEffect, useRef, useState, type ReactNode } from 'react';

import { links, me, support } from '../api/endpoints.ts';
import { formatPara, para } from '../core/money.ts';
import { useStore } from '../core/store.ts';
import { errorText, LANG_TITLES, LANGS, setLang, useLang, useStrings } from '../i18n/index.ts';
import { selectedAddress, useAddressBook } from '../state/address-book.ts';
import { defaultCard, paymentConfigStore, savedCardsStore } from '../state/payment.ts';
import { openSignIn, pushRoute } from '../state/router.ts';
import { deleteAccount, showsLanguagePicker, signOut, unlockLanguagePicker, useSession } from '../state/session.ts';
import { loadNotificationSettings, notificationsStore, updateNotificationSettings } from '../state/stores.ts';
import { Icon, type IconName } from '../ui/icons.tsx';
import { BankBadge } from '../ui/BankBadge.tsx';
import { Button, Card, ConfirmDialog, cx, ErrorBanner, ListRow, ScreenTitle, Toggle } from '../ui/kit.tsx';
import { PayMarks } from '../ui/PayMarks.tsx';
import { Screen } from '../ui/Screen.tsx';
import { EditProfileSheet, ProfileAvatar, profileInitial } from './EditProfileSheet.tsx';
import { BrandLockup } from './SignInSheet.tsx';
import '../styles/account.css';

/** Сколько касаний заголовка открывают выбор языка. */
const LANGUAGE_UNLOCK_TAPS = 10;

/**
 * Профиль: имя и телефон, заказы, адреса, избранное, бонусы, поддержка, уведомления двумя
 * независимыми переключателями, язык, условия и политика, выход и удаление аккаунта. Гость
 * видит приглашение войти, адрес, язык и юридические ссылки.
 */
export function ProfileScreen() {
  const strings = useStrings();
  const lang = useLang();
  const session = useSession();
  const addressBook = useAddressBook();
  const notifications = useStore(notificationsStore);

  const profile = session.profile;
  const signedIn = profile !== null;
  const addressText = selectedAddress(addressBook)?.text;

  const [showsEdit, setShowsEdit] = useState(false);
  const [dialog, setDialog] = useState<'signOut' | 'delete' | null>(null);
  const [isLeaving, setIsLeaving] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // Баланс бонусов и непрочитанный ответ поддержки: подписи строк профиля.
  const [bonusBalance, setBonusBalance] = useState<string | null>(null);
  const [supportUnread, setSupportUnread] = useState(false);
  const titleTaps = useRef(0);

  useEffect(() => {
    if (!signedIn) {
      setBonusBalance(null);
      setSupportUnread(false);
      return;
    }
    let stale = false;
    void loadNotificationSettings();
    void me
      .credits()
      .then((credits) => {
        const balance = para(credits.balanceRsd);
        if (!stale) setBonusBalance(balance > 0n ? formatPara(balance) : null);
      })
      .catch(() => undefined);
    void support
      .get(null)
      .then((view) => {
        if (!stale) setSupportUnread(view.hasUnread ?? false);
      })
      .catch(() => undefined);
    return () => {
      stale = true;
    };
  }, [signedIn]);

  const tapTitle = () => {
    titleTaps.current += 1;
    if (titleTaps.current >= LANGUAGE_UNLOCK_TAPS) unlockLanguagePicker();
  };

  // Корзину, адреса, избранное и уведомления человека стирает связка хранилищ по смене сессии.
  const leave = async (action: () => Promise<void>) => {
    setIsLeaving(true);
    setDeleteError(null);
    try {
      await action();
    } catch (failure) {
      setDeleteError(errorText(strings, failure));
    } finally {
      setIsLeaving(false);
      setDialog(null);
    }
  };

  // Идентификатор аккаунта: телефон или e-mail Apple-аккаунта; телефон для курьера из заказа — не аккаунт.
  const contactLine = profile?.phone ?? profile?.email ?? profile?.contactPhone ?? '';
  const paymentConfig = useStore(paymentConfigStore);
  useStore(savedCardsStore);
  const addressesRow = <ListRow icon="pin" title={strings.myAddresses} value={addressText} chevron onClick={() => pushRoute({ name: 'addresses' })} />;
  const card = defaultCard();
  const cardsRow = paymentConfig.savedCards && (
    <ListRow
      icon="card"
      title={strings.paymentMethods}
      value={card ? <span className="row profile-card-value"><BankBadge issuer={card.issuer} brand={card.brand} size={18} /><span className="t-numeric">·· {card.last4 ?? '????'}</span></span> : undefined}
      chevron
      onClick={() => pushRoute({ name: 'cards' })}
    />
  );

  return (
    <Screen>
      <div className="readable stack profile">
        <div onClick={tapTitle}>
          <ScreenTitle text={strings.profileTitle} />
        </div>

        <div className="gutter stack profile-groups">
          {profile ? (
            <button type="button" className="row profile-header" aria-label={strings.editProfile} onClick={() => setShowsEdit(true)}>
              <ProfileAvatar url={profile.avatarUrl} initial={profileInitial(profile.name)} size={64} />
              {/* Без имени заголовком служит телефон. */}
              <span className="grow stack">
                <span className="t-display profile-name">{profile.name || contactLine}</span>
                {profile.name && <span className="t-numeric t-muted profile-contact">{contactLine}</span>}
              </span>
              <span className="profile-edit">
                <Icon name="pencil" size={15} weight={2.4} />
              </span>
            </button>
          ) : (
            <Card className="stack guest-card">
              <BrandLockup size={40} />
              <h2 className="t-display">{strings.signInToContinue}</h2>
              <p className="t-subheadline t-muted">{strings.guestProfileHint}</p>
              <Button onClick={openSignIn}>{strings.signIn}</Button>
            </Card>
          )}

          {signedIn ? (
            <>
              <Card flush>
                <ListRow icon="box" title={strings.ordersTitle} chevron onClick={() => pushRoute({ name: 'orders' })} />
                {addressesRow}
                {cardsRow}
                <ListRow
                  icon="heartFill"
                  title={strings.favoritesTitle}
                  chevron
                  onClick={() => pushRoute({ name: 'list', source: { kind: 'favorites' }, title: strings.favoritesTitle })}
                />
                <ListRow
                  icon="history"
                  title={strings.youBought}
                  chevron
                  onClick={() => pushRoute({ name: 'list', source: { kind: 'purchases' }, title: strings.youBought })}
                />
                <ListRow icon="gift" title={strings.bonusBalance} value={bonusBalance} chevron onClick={() => pushRoute({ name: 'support', orderId: null })} />
                <ListRow
                  icon="chat"
                  title={strings.supportTitle}
                  value={supportUnread ? <span className="profile-unread" /> : null}
                  chevron
                  onClick={() => pushRoute({ name: 'support', orderId: null })}
                />
              </Card>

              <Card flush>
                <p className="profile-section-title">{strings.notificationsTitle}</p>
                <ToggleRow
                  title={strings.notifyOrders}
                  hint={strings.notifyOrdersHint}
                  checked={notifications.settings.orders}
                  onChange={(orders) => void updateNotificationSettings({ orders })}
                />
                <ToggleRow
                  title={strings.notifyPromo}
                  hint={strings.notifyPromoHint}
                  checked={notifications.settings.promo}
                  onChange={(promo) => void updateNotificationSettings({ promo })}
                />
              </Card>
            </>
          ) : (
            <Card flush>
              {addressesRow}
              {cardsRow}
            </Card>
          )}

          {showsLanguagePicker(session) && (
            <Card flush>
              <div className="list-row">
                <Icon name="globe" className="lead-icon" size={22} />
                <span className="grow profile-row-title">{strings.language}</span>
                <span className="segmented" role="radiogroup" aria-label={strings.language}>
                  {LANGS.map((item) => (
                    <button key={item} type="button" role="radio" aria-checked={item === lang} className={cx(item === lang && 'is-selected')} onClick={() => setLang(item)}>
                      {LANG_TITLES[item]}
                    </button>
                  ))}
                </span>
              </div>
            </Card>
          )}

          <Card flush>
            <LinkRow icon="doc" title={strings.termsOfUse} href={links.terms(lang)} />
            <LinkRow icon="shield" title={strings.privacyPolicy} href={links.privacy(lang)} />
          </Card>

          <PayMarks caption={strings.cardsAccepted} />

          {signedIn && (
            <Card flush>
              <button type="button" className="list-row profile-leave is-danger" onClick={() => setDialog('signOut')}>
                {strings.signOut}
              </button>
              <button type="button" className="list-row profile-leave" onClick={() => setDialog('delete')}>
                {strings.deleteAccount}
              </button>
            </Card>
          )}

          {deleteError && <ErrorBanner text={deleteError} />}
        </div>
      </div>

      {showsEdit && <EditProfileSheet onClose={() => setShowsEdit(false)} />}
      {dialog === 'signOut' && (
        <ConfirmDialog
          title={strings.signOutConfirm}
          confirmTitle={strings.signOut}
          destructive
          busy={isLeaving}
          onConfirm={() => void leave(signOut)}
          onCancel={() => setDialog(null)}
        />
      )}
      {dialog === 'delete' && (
        <ConfirmDialog
          title={strings.deleteAccount}
          text={strings.deleteAccountConfirm}
          confirmTitle={strings.deleteAccount}
          destructive
          busy={isLeaving}
          onConfirm={() => void leave(deleteAccount)}
          onCancel={() => setDialog(null)}
        />
      )}
    </Screen>
  );
}

function ToggleRow(props: { title: string; hint: string; checked: boolean; onChange: (next: boolean) => void }) {
  return (
    <div className="list-row">
      <span className="grow stack">
        <span className="profile-row-title">{props.title}</span>
        <span className="t-footnote t-muted">{props.hint}</span>
      </span>
      <Toggle checked={props.checked} label={props.title} onChange={props.onChange} />
    </div>
  );
}

/** Строка-ссылка на страницу сайта: открывается в новой вкладке. */
function LinkRow(props: { icon: IconName; title: ReactNode; href: string }) {
  return (
    <a className="list-row" href={props.href} target="_blank" rel="noreferrer">
      <Icon name={props.icon} className="lead-icon" size={22} />
      <span className="grow profile-row-title">{props.title}</span>
      <svg className="chevron" width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M7 17 17 7M8 7h9v9" />
      </svg>
    </a>
  );
}
